import { type ReactNode, type RefObject, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  autoUpdate,
  type ClientRectObject,
  flip,
  hide,
  type Middleware,
  offset,
  type Placement,
  shift,
  useFloating,
  type VirtualElement,
} from '@floating-ui/react-dom';

import { getDocument } from '@/lib/utils';

const VIEWPORT_MARGIN_PX = 8;
const TOOLBAR_GAP_PX = 4;

const EMPTY_RECT: ClientRectObject = {
  x: 0,
  y: 0,
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  width: 0,
  height: 0,
};

function createLiveAnchor(anchorRef: RefObject<HTMLElement | null>): VirtualElement {
  return {
    getBoundingClientRect: () => anchorRef.current?.getBoundingClientRect() ?? EMPTY_RECT,
    get contextElement() {
      return anchorRef.current ?? undefined;
    },
  };
}

function isDomElement(value: unknown): value is Element {
  return typeof Element !== 'undefined' && value instanceof Element;
}

function getReferenceElement(reference: Element | VirtualElement | null): Element | null {
  if (isDomElement(reference)) return reference;
  return reference?.contextElement ?? null;
}

type ToolbarPlacement = 'above' | 'below' | 'right' | 'left' | 'inside-top-start';

function mapPlacement(placement: ToolbarPlacement): Placement {
  switch (placement) {
    case 'left':
      return 'left-start';
    case 'right':
      return 'right-start';
    case 'below':
      return 'bottom';
    default:
      return 'top-start';
  }
}

function getPageEditorHeaderRect(): DOMRect | null {
  const doc = getDocument();
  return (
    doc?.querySelector<HTMLElement>('[data-page-editor-header]')?.getBoundingClientRect() ?? null
  );
}

function getShiftPadding() {
  const headerRect = getPageEditorHeaderRect();
  return {
    top: Math.max(VIEWPORT_MARGIN_PX, Math.round((headerRect?.bottom ?? 0) + TOOLBAR_GAP_PX)),
    left: Math.max(VIEWPORT_MARGIN_PX, Math.round(headerRect?.left ?? VIEWPORT_MARGIN_PX)),
    right: VIEWPORT_MARGIN_PX,
    bottom: VIEWPORT_MARGIN_PX,
  };
}

function getCarouselBoundary(reference: Element | null) {
  if (!reference) return undefined;
  return (
    reference.closest('[data-slot="carousel-content"]') ??
    reference.closest('[data-slot="carousel-item"]')
  );
}

function getAnchorViewportRect(
  reference: Element | null | undefined,
  fallback: { x: number; y: number; width: number; height: number },
) {
  if (isDomElement(reference)) {
    const rect = reference.getBoundingClientRect();
    return {
      top: rect.top,
      bottom: rect.bottom,
      left: rect.left,
      right: rect.right,
    };
  }

  return {
    top: fallback.y,
    bottom: fallback.y + fallback.height,
    left: fallback.x,
    right: fallback.x + fallback.width,
  };
}

function isToolbarAnchorOutOfViewport(
  anchorTop: number,
  anchorBottom: number,
  minTop: number,
  viewportBottom: number,
) {
  return anchorBottom <= minTop || anchorTop >= viewportBottom;
}

/** Treats the fixed header as part of the viewport boundary when checking anchor visibility. */
const editorToolbarVisibility: Middleware = {
  name: 'editorToolbarVisibility',
  fn({ rects, elements }) {
    const doc = getDocument();
    const viewport = doc?.defaultView;
    if (!viewport) {
      return { data: { hidden: true } };
    }

    const padding = getShiftPadding();
    const viewportBottom = viewport.innerHeight - VIEWPORT_MARGIN_PX;
    const anchorRect = getAnchorViewportRect(
      getReferenceElement(elements.reference),
      rects.reference,
    );
    const hidden = isToolbarAnchorOutOfViewport(
      anchorRect.top,
      anchorRect.bottom,
      padding.top,
      viewportBottom,
    );

    return { data: { hidden } };
  },
};

/** Separate from editorToolbarVisibility so clamping position never marks the anchor hidden. */
const editorToolbarPosition: Middleware = {
  name: 'editorToolbarPosition',
  fn({ y, rects }) {
    const doc = getDocument();
    const viewport = doc?.defaultView;
    if (!viewport) return {};

    const padding = getShiftPadding();
    const minTop = padding.top;
    const maxTop = Math.max(
      minTop,
      viewport.innerHeight - VIEWPORT_MARGIN_PX - rects.floating.height,
    );

    return {
      y: Math.min(Math.max(y, minTop), maxTop),
    };
  },
};

interface FloatingBlockToolbarPortalProps {
  anchorRef: RefObject<HTMLElement | null>;
  children: ReactNode;
  placement?: ToolbarPlacement;
  clampToViewport?: boolean;
}

export function FloatingBlockToolbarPortal({
  anchorRef,
  children,
  placement = 'above',
  clampToViewport = true,
}: FloatingBlockToolbarPortalProps) {
  const floatingPlacement = mapPlacement(placement);
  const isSidePlacement = placement === 'left' || placement === 'right';
  const isInsidePlacement = placement === 'inside-top-start';
  const liveAnchor = useMemo(() => createLiveAnchor(anchorRef), [anchorRef]);

  const middleware = useMemo(
    () => [
      offset(({ rects }) =>
        isInsidePlacement ? -rects.floating.height - TOOLBAR_GAP_PX : TOOLBAR_GAP_PX,
      ),
      ...(clampToViewport ? [] : [flip()]),
      shift((state) => {
        const reference = getReferenceElement(state.elements.reference);
        const carouselBoundary =
          isSidePlacement && reference ? getCarouselBoundary(reference) : undefined;

        return {
          padding: getShiftPadding(),
          boundary: carouselBoundary ?? 'clippingAncestors',
          rootBoundary: 'viewport',
        };
      }),
      hide({ strategy: 'referenceHidden' }),
      editorToolbarVisibility,
      ...(clampToViewport ? [editorToolbarPosition] : []),
    ],
    [clampToViewport, isInsidePlacement, isSidePlacement],
  );

  const { refs, floatingStyles, middlewareData, isPositioned } = useFloating<VirtualElement>({
    placement: floatingPlacement,
    strategy: 'fixed',
    transform: false,
    middleware,
    elements: { reference: liveAnchor },
    whileElementsMounted: (reference, floating, updatePosition) =>
      autoUpdate(reference, floating, updatePosition, { animationFrame: true }),
  });

  const isHidden =
    !isPositioned ||
    middlewareData.hide?.referenceHidden === true ||
    middlewareData.editorToolbarVisibility?.hidden === true;

  const doc = getDocument();
  // Mount inside the editor's isolated stacking context (#cms-layout) so the
  // peeked sidebar can paint above this toolbar. Fall back to body outside the CMS.
  const mountNode = doc?.getElementById('cms-layout') ?? doc?.body ?? null;

  if (!mountNode) {
    return null;
  }

  return createPortal(
    <div
      ref={refs.setFloating}
      data-rich-text-editor-ui="block-toolbar-root"
      className="pointer-events-auto z-[121] flex items-start"
      style={{
        ...(isHidden ? undefined : floatingStyles),
        display: isHidden ? 'none' : undefined,
      }}
    >
      {children}
    </div>,
    mountNode,
  );
}
